/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/naming-convention */
import { TabContext } from '@mui/lab';
import React, { useEffect, useRef, useState } from 'react';
import api from '../../api/base';
import { alertsStats, readsStats } from '../../api/paths';
import { AlertsResp, AlertsResult, ReadsResp, ReadsResult } from '../../api/paths.types';
import { Sidebar } from '../../components/Sidebar';
import { alertsResultInit, availableIntervals, initAvailableFilters, initFilt, intervals, readsResultInit } from './Charts.constants';
import { AggregatedAlertsData, AggregatedReadsData, AvailableFilters, IntervalType, PickedFilters, PreparedDataType } from './Charts.types';
import { ChartsContent } from './Charts.styled';
import Header from './Header/Header';
import LineGraph from './LineGraph';
import moment from 'moment';
import HeatmapTab from './HeatmapTab';
import ExportPdfModal, { ExportPdfModalRefType } from './ExportPdfModal';
import TableTab from './TableTab';
import { TabPanel } from './CustomTablePanel';
import { useDebounce } from 'use-debounce/lib';
import useError from '../../hooks/useError';

const Charts = () => {
  const [pickedFilters, setPickedFilters] = useState<PickedFilters>(initFilt);
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters>(initAvailableFilters);
  const [debauncedPickedFilters] = useDebounce(pickedFilters, 1000);

  const [intervalForReads, setIntervalForReads] = useState<IntervalType>('1d');
  const [intervalForAlerts, setIntervalForAlerts] = useState<IntervalType>('1d');

  const [alertsData, setAlertsData] = useState<AggregatedAlertsData>(alertsResultInit);
  const [alertsStatsData, setAlertsStatsData] = useState<Array<Record<string, string | number>>>([]);
  const [readsData, setReadsData] = useState<AggregatedReadsData>(readsResultInit);
  const [readsStatsData, setReadsStatsData] = useState<Array<Record<string, string | number>>>([]);

  const [activeTab, setActiveTab] = useState<string>('1');

  const exportPdfModal = useRef<ExportPdfModalRefType>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  const [handleError] = useError();

  const handleSwitchTab = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  // Get all data for filters
  const fetchInitialFilters = async () => {
    try {
      const { data: { results } } = await api.get<ReadsResp>(readsStats, {
        params: {
          created__gte: `now-${initFilt.duration}`,
          interval: intervals[1],
        },
      });
      const [newResults, readsStat] = transformData(results);
      const alertData = await api.get<AlertsResp>(alertsStats, {
        params: {
          created__gte: `now-${initFilt.duration}`,
          interval: intervals[1],
        },
      });
      const { alert_type_id } = alertData.data.results;

      setReadsStatsData(readsStat as Array<Record<string, string | number>>);
      setReadsData(newResults as AggregatedReadsData);
      setAvailableFilters({
        ...availableFilters,
        cameras: results?.camera_id,
        site: results?.client_id,
        alerts: alert_type_id,
      });
    } catch (err: any) {
      handleError(err);
    }
  };

  // Data format from BE is not suitable for Table view (but fine for charts)
  // That's why we need to convert different data sources to one array
  const transformData = (rawData: ReadsResult | AlertsResult) => {
    // Each interval have specific convertion date
    const dateFormat = availableIntervals.find((ai) => ai.value === (('alert_type_id' in rawData) ? intervalForAlerts : intervalForReads));

    // We will create object where keys are timestamp
    // That way we can add all different data from all sources
    const emptyObjectCamera: PreparedDataType = {};

    // For future use of statistic we need to store information about total information
    // It's apply to alerts as well
    const totalCameraData = { ...emptyObjectCamera, total: 0, id: 'camera' };

    // First of all we need to create empty object with all data.
    // All cameras \ all sites \ all alert classes - that way won't have missing keys
    // We can switch to nulls later if we will need to
    rawData.camera_id.forEach((camera) => {
      emptyObjectCamera[camera.name] = 0;
      totalCameraData[camera.name] = (totalCameraData[camera.name] || 0) + camera.count;
      totalCameraData.total += camera.count;
    });


    const cameraData = rawData.aggregation_camera_id.map((camera) => {
      let prepData: any = {};
      prepData['correctDate'] = moment(camera.key_as_string).format(dateFormat!.dateFormat);

      camera.buckets.forEach((bucket) => {
        if (pickedFilters.cameras.includes(bucket.id)) {
          prepData = {
            ...emptyObjectCamera,
            ...prepData,
            [bucket.name]: bucket.count,
          };
        }
      });

      return prepData;
    });

    // The same for site as ^^
    const emptyObjectSite: PreparedDataType = {};
    const totalSiteData = { ...emptyObjectSite, total: 0, id: 'site' };

    rawData.client_id.forEach((site) => {
      emptyObjectSite[site.name] = 0;
      totalSiteData[site.name] = (totalSiteData[site.name] || 0) + site.count;
      totalSiteData.total += site.count;
    });


    const siteData = rawData.aggregation_client_id.map((site) => {
      let prepData: any = {};

      prepData['correctDate'] = moment(site.key_as_string).format(dateFormat!.dateFormat);

      site.buckets.forEach((bucket) => {
        if (pickedFilters.site.includes(bucket.id)) {
          prepData = {
            ...emptyObjectSite,
            ...prepData,
            [bucket.name]: bucket.count,
          };
        }
      });

      if (site.buckets.length === 0) {
        prepData = {
          ...emptyObjectSite,
          ...prepData,
        };
      }

      return prepData;
    });

    // We need to convirt plain objects with lat, lng to google maps one + add weight there
    // For more info
    // https://developers.google.com/maps/documentation/javascript/heatmaplayer#add_weighted_data_points
    const heatmapData = rawData.heatmap.map((res) => ({ weight: res.count, location: new google.maps.LatLng(+res.location.lat, +res.location.lon) }));

    // Alerts and reads have only one differnce - it's alert_type_id field with all the data for each alert class
    // Function is common for both of them
    // If rawData that is passed to this function has alert data
    // We do the same as before
    if ('alert_type_id' in rawData) {
      const emptyObjectAlert: PreparedDataType = {};
      const totalAlertsData = { ...emptyObjectAlert, total: 0, id: 'alerts' };

      rawData.alert_type_id.forEach((alert) => {
        emptyObjectAlert[alert.name] = 0;

        totalAlertsData[alert.name] = (totalAlertsData[alert.name] || 0) + alert.count;
        totalAlertsData.total += alert.count;
      });
      const alertPrepData = rawData.aggregation_alert_type_id.map((alert) => {
        let prepData: any = {};

        prepData['correctDate'] = moment(alert.key_as_string).format(dateFormat!.dateFormat);

        alert.buckets.forEach((bucket) => {
          if (pickedFilters.alerts.includes(bucket.id)) {
            prepData = {
              ...emptyObjectAlert,
              ...prepData,
              [bucket.name]: bucket.count,
            };
          }
        });

        if (alert.buckets.length === 0) {
          prepData = {
            ...emptyObjectAlert,
            ...prepData,
          };
        }

        return prepData;
      });


      const newResults = {
        heatmapData: heatmapData as google.maps.visualization.WeightedLocation[],
        camerasData: cameraData,
        sitesData: siteData,
        alertsData: alertPrepData,
      } as AggregatedAlertsData;
      return [newResults, [totalSiteData, totalCameraData, totalAlertsData]];
    }
    const newResults = {
      heatmapData: heatmapData as google.maps.visualization.WeightedLocation[],
      camerasData: cameraData,
      sitesData: siteData,
    } as AggregatedReadsData;
    return [newResults, [totalSiteData, totalCameraData]];
  };

  const fetchReadsData = async () => {
    setLoading(true);
    if (pickedFilters.site) {
      try {
        const { data: { results } } = await api.get<ReadsResp>(readsStats, {
          params: {
            interval: intervalForReads,
            created__gte: pickedFilters.fromDate || `now-${pickedFilters.duration}`,
            created__lte: pickedFilters.toDate || null,
            client_id__in: pickedFilters.site || null,
            camera_id__in: pickedFilters.cameras.join('__') || null,
          },
        });

        const [newResults, readsStat] = transformData(results);

        setReadsStatsData(readsStat as Array<Record<string, string | number>>);
        setReadsData(newResults as AggregatedReadsData);
        setAvailableFilters({
          ...availableFilters,
          cameras: results.camera_id,
        });
        setLoading(false);
      } catch (err: any) {
        handleError(err);
        setLoading(false);
      }
    } else {
      setReadsData(readsResultInit);
      setLoading(false);
    }
  };

  const fetchAlertsData = async () => {
    setLoadingAlerts(true);
    if (pickedFilters.site.length && pickedFilters.cameras.length && pickedFilters.alerts.length) {
      try {
        const { data: { results } } = await api.get<ReadsResp>(alertsStats, {
          params: {
            interval: intervalForAlerts,
            created__gte: pickedFilters.fromDate || `now-${pickedFilters.duration}`,
            created__lte: pickedFilters.toDate || null,
            client_id__in: pickedFilters.site || null,
            camera_id__in: pickedFilters.cameras.join('__') || null,
            alert_type_id__in: pickedFilters.alerts.join('__') || null,
          },
        });

        const [newResults, alertStats] = transformData(results);

        setAlertsStatsData(alertStats as Array<Record<string, string | number>>);
        setAlertsData(newResults as AggregatedAlertsData);
        setLoadingAlerts(false);
      } catch (err: any) {
        handleError(err);
        setLoadingAlerts(false);
      }
    } else {
      setAlertsData(alertsResultInit);
      setLoadingAlerts(false);
    }
  };

  useEffect(() => {
    fetchReadsData();
  }, [
    debauncedPickedFilters.cameras,
    debauncedPickedFilters.duration,
    debauncedPickedFilters.fromDate,
    debauncedPickedFilters.toDate,
    debauncedPickedFilters.site,
    intervalForReads,
  ]);

  useEffect(() => {
    fetchAlertsData();
  }, [debauncedPickedFilters, intervalForAlerts]);

  useEffect(() => {
    setTimeout(() => {
      fetchInitialFilters();
    }, 200);
  }, []);

  const [readsMode, setReadsMode] = useState<'site' | 'camera'>('site');
  const [alertsMode, setAlertsMode] = useState<'site' | 'camera' | 'alert'>('alert');
  return (
    <Sidebar>
      <TabContext value={activeTab}>
        <Header
          exportPdfModal={exportPdfModal}
          handleSwitchTab={handleSwitchTab}
          pickedFilters={pickedFilters}
          availableFilters={availableFilters}
          setPickedFilters={setPickedFilters}
        />
        <ChartsContent>
          <TabPanel value="1">
            <LineGraph
              setIntervalForReads={setIntervalForReads}
              intervalForReads={intervalForReads}
              intervalForAlerts={intervalForAlerts}
              setIntervalForAlerts={setIntervalForAlerts}
              type={pickedFilters.type}
              readsData={readsData}
              alertsData={alertsData}
              pickedFilters={pickedFilters}
              availableFilters={availableFilters}
              setPickedFilters={setPickedFilters}
              loading={loading}
              loadingAlerts={loadingAlerts}
            />
          </TabPanel>
          <TabPanel value="2">
            <TableTab
              setIntervalForReads={setIntervalForReads}
              intervalForReads={intervalForReads}
              intervalForAlerts={intervalForAlerts}
              setIntervalForAlerts={setIntervalForAlerts}
              type={pickedFilters.type}
              readsData={readsData}
              alertsData={alertsData}
              pickedFilters={pickedFilters}
              availableFilters={availableFilters}
              setPickedFilters={setPickedFilters}
              loading={loading}
              readsMode={readsMode}
              setReadsMode={setReadsMode}
              alertsMode={alertsMode}
              setAlertsMode={setAlertsMode}
            />
          </TabPanel>
          <TabPanel value="3">
            <HeatmapTab
              type={pickedFilters.type}
              heatmapDataReads={readsData.heatmapData}
              heatmapDataAlerts={alertsData.heatmapData}
              pickedFilters={pickedFilters}
              availableFilters={availableFilters}
              setPickedFilters={setPickedFilters}
              loading={loading}
            />
          </TabPanel>
        </ChartsContent>
      </TabContext>
      <ExportPdfModal
        duration={pickedFilters.duration}
        ref={exportPdfModal}
        alertsStats={alertsStatsData}
        readsStats={readsStatsData}
        readsData={readsData}
        alertsData={alertsData}
        pickedFilters={pickedFilters}
        readsMode={readsMode}
        alertsMode={alertsMode}
      />
    </Sidebar>
  );
};

export default Charts;
